import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import api from '../services/api';
import { formatCurrency } from '../utils/format';
import Loading from './Loading';

/**
 * Colorblind-safe palette (Wong) for multi-vendor lines.
 * @constant {string[]}
 */
const LINE_COLORS = [
  '#0077BB', '#33BBEE', '#EE7733', '#CC3311',
  '#EE3377', '#009988', '#BBBBBB'
];

/**
 * Displays a line chart of price history for a specific item.
 * Groups data by vendor to show multiple lines if the same item
 * is purchased from different vendors.
 *
 * @param {Object} props
 * @param {string} props.itemName - The item name to show price history for
 * @param {string} [props.sku] - Optional SKU to filter by
 * @param {Function} props.onClose - Callback to close/hide the chart
 * @returns {JSX.Element}
 */
function PriceHistoryChart({ itemName, sku, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 11.1 — toggle the y-axis between raw unit_price and the
  // auto-derived normalized_price ($/canonical_unit). Cross-vendor
  // comparison only makes sense in normalized $.
  const [showNormalized, setShowNormalized] = useState(false);

  /**
   * Fetch price history data from the API.
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (itemName) params.item_name = itemName;
      if (sku) params.sku = sku;
      const res = await api.getPriceHistory(params);
      setData(res.data);
    } catch (err) {
      console.error('Error loading price history:', err);
      setError('Failed to load price history');
    } finally {
      setLoading(false);
    }
  }, [itemName, sku]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <Loading variant="spinner" message="Loading price history..." />;
  if (error) return <div className="empty-state"><span className="empty-state__title">{error}</span></div>;
  if (data.length === 0) {
    return (
      <div className="empty-state">
        <span className="empty-state__title">No price history found</span>
        <span className="empty-state__description">No historical pricing data for this item.</span>
      </div>
    );
  }

  // Pick which value series drives the y-axis. When normalized mode is on
  // we use the backend-computed normalized_price (auto-derived from each
  // line's UOM). NULLs are passed through so spanGaps keeps the line
  // continuous over rows where normalization isn't computable.
  const yField = showNormalized ? 'normalized_price' : 'unit_price';
  const canonicalUnit = showNormalized
    ? (data.find(d => d.canonical_unit)?.canonical_unit || 'unit')
    : null;

  /**
   * Build chart series. Two cases need to look different:
   *
   * 1) Same-day duplicates (Terry Towel — GH #177): two physically
   *    different products that OCR collapsed under the same
   *    item_name+SKU each appear on the SAME invoice/date with
   *    different prices. Splitting by (vendor, price tier) keeps both
   *    visible instead of overwriting one with the other.
   *
   * 2) Time-based price changes (SIG SOAP SVC — GH #180): the SAME
   *    product appears once per invoice; the price varies over time.
   *    Ruth expects a single line that dips and recovers. Splitting by
   *    price tier here breaks the line into multiple flat segments and
   *    HIDES the time-based change.
   *
   * Resolution: only tier-split a vendor when at least one date has
   * multiple distinct prices for that vendor. Otherwise emit one line
   * per vendor (the original behavior, pre-#177).
   */
  const allDates = [...new Set(data.map(d => d.invoice_date))].sort();

  // Index entries by vendor → date → list of values.
  const byVendor = {};
  data.forEach(entry => {
    const vendor = entry.vendor_name || 'Unknown';
    const v = entry[yField] != null ? Number(entry[yField]) : null;
    if (!byVendor[vendor]) byVendor[vendor] = { dateValues: {}, entries: [] };
    if (!byVendor[vendor].dateValues[entry.invoice_date]) {
      byVendor[vendor].dateValues[entry.invoice_date] = [];
    }
    byVendor[vendor].dateValues[entry.invoice_date].push(v);
    byVendor[vendor].entries.push(entry);
  });

  /**
   * Returns true when ANY single date for this vendor has 2+ distinct
   * (non-null, rounded-to-2-decimals) values — i.e. the Terry Towel
   * "two products on the same invoice" pattern.
   */
  function vendorHasSameDayDuplicates(dateValues) {
    return Object.values(dateValues).some((vals) => {
      const distinct = new Set(
        vals.filter(v => v != null).map(v => v.toFixed(2))
      );
      return distinct.size > 1;
    });
  }

  const datasets = [];
  let colorIdx = 0;
  Object.entries(byVendor).forEach(([vendor, { dateValues, entries }]) => {
    if (vendorHasSameDayDuplicates(dateValues)) {
      // Tier split (Terry Towel case). Group this vendor's entries by
      // rounded price and emit one series per distinct tier.
      const byTier = {};
      entries.forEach(e => {
        const v = e[yField] != null ? Number(e[yField]) : null;
        const tierKey = v == null ? 'null' : v.toFixed(2);
        if (!byTier[tierKey]) byTier[tierKey] = { tierPrice: v, dateMap: {} };
        byTier[tierKey].dateMap[e.invoice_date] = v;
      });
      Object.values(byTier).forEach(({ tierPrice, dateMap }) => {
        datasets.push({
          label: tierPrice != null ? `${vendor} ($${tierPrice.toFixed(2)})` : vendor,
          data: allDates.map(d => dateMap[d] ?? null),
          borderColor: LINE_COLORS[colorIdx++ % LINE_COLORS.length],
          backgroundColor: 'transparent',
          tension: 0.3,
          spanGaps: true,
          pointRadius: 3
        });
      });
    } else {
      // Single line per vendor (SIG SOAP SVC case + the common case
      // of one product over time). Picks the first non-null value when
      // a date has multiple identical entries — which by definition all
      // have the same rounded value here, so the choice doesn't matter.
      const dateMap = {};
      entries.forEach(e => {
        if (dateMap[e.invoice_date] == null) {
          dateMap[e.invoice_date] = e[yField] != null ? Number(e[yField]) : null;
        }
      });
      datasets.push({
        label: vendor,
        data: allDates.map(d => dateMap[d] ?? null),
        borderColor: LINE_COLORS[colorIdx++ % LINE_COLORS.length],
        backgroundColor: 'transparent',
        tension: 0.3,
        spanGaps: true,
        pointRadius: 3
      });
    }
  });

  const chartData = { labels: allDates, datasets };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: (ctx) => showNormalized
            ? `${ctx.dataset.label}: $${Number(ctx.parsed.y).toFixed(2)}/${canonicalUnit}`
            : `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
        }
      }
    },
    scales: {
      // GH #184: Ruth UAT — X-axis labels were displayed as raw ISO strings
      // and Chart.js was truncating them so all she could see was the day
      // number ("01", "16", "18"), making the chart look ordered by day-of-
      // month instead of by full date. Format as "Mon D, YYYY" so the year
      // and month are unambiguous, and turn off auto-skip with a 45° rotation
      // so every point label stays readable.
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          minRotation: 30,
          callback: function(value) {
            // Chart.js category scale passes the INDEX, not the label string.
            // Read the label off the chart instance to format it.
            const raw = this.getLabelForValue(value);
            if (!raw || typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(raw)) {
              return raw;
            }
            const [y, m, d] = raw.slice(0, 10).split('-').map(Number);
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${months[m - 1]} ${d}, ${y}`;
          }
        }
      },
      y: {
        ticks: {
          // GH #189: Ruth UAT — Y-axis labels need 2 decimals so small
          // increments ($0.43 vs $0.45 vs $0.58) are distinguishable on
          // low-priced items. The previous compact format ($1.2K, $0.5)
          // hid the cent-level precision she needs to read price drift.
          callback: (val) => showNormalized
            ? `$${Number(val).toFixed(2)}/${canonicalUnit}`
            : formatCurrency(val)
        }
      }
    }
  };

  // True when at least one row in the dataset has a usable normalized_price.
  // If every row has NULL normalized_price we don't show the toggle — there's
  // nothing to switch to.
  const hasNormalized = data.some(d => d.normalized_price != null);

  return (
    <div className="card">
      <div className="card-header">
        <h3>Price History: {itemName || sku}</h3>
        <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
          {hasNormalized && (
            <label style={{ display: 'flex', gap: 'var(--sp1)', alignItems: 'center', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={showNormalized}
                onChange={e => setShowNormalized(e.target.checked)}
                aria-label="Show normalized per-unit price"
              />
              Show $/unit
            </label>
          )}
          {onClose && (
            <button className="btn-small btn-outline" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
      <div
        className="chart-container"
        role="img"
        aria-label={`Price history line chart for ${itemName || sku}${
          data.length ? `, ${data.length} data point${data.length === 1 ? '' : 's'} across ${datasets.length} vendor line${datasets.length === 1 ? '' : 's'}` : ''
        }`}
      >
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}

PriceHistoryChart.propTypes = {
  /** The item name to show price history for */
  itemName: PropTypes.string,
  /** Optional SKU to filter by */
  sku: PropTypes.string,
  /** Callback to close/hide the chart */
  onClose: PropTypes.func
};

export default PriceHistoryChart;
